import { getApiGalaxyServer } from 'services';
import ApiClientManager from 'core/apiClient';

export interface GetTeamMemberCategoryCountsPayload {
  teamId: number;
  /** Optional tenant for scoping the API call to the correct realm/tenant */
  tenant?: string;
  /** Optional realmId for scoping the API call to the correct realm/tenant */
  realmId?: string;
}

/**
 * Fetch a single team's member counts broken down by employment type
 * (basic member / internal contractor / external contractor / guest).
 *
 * Uses the existing overall count statistics endpoint scoped to one team via
 * `team_ids`. Date params are intentionally omitted so the `created_at` filter
 * is a no-op and we get the team's standing headcount rather than a
 * date-windowed subset. The response exposes these under
 * `count_statistics.team_memberships.filtered_counts`.
 *
 * @see src/modules/StatisticsModule/query/hooks/useTeamMemberCategoryCountsQuery.tsx
 */
export const getTeamMemberCategoryCountsApi = ({
  params,
  token,
  config,
}: BaseApiParams<GetTeamMemberCategoryCountsPayload>) => {
  const { teamId, tenant, realmId } = params;

  // Build custom headers if tenant/realmId are provided so the call targets the
  // right realm even when the URL context is not set (mirrors searchTeamsByFieldsApi).
  const customHeaders =
    tenant && realmId
      ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
      : undefined;

  return getApiGalaxyServer(
    '/rails_api/statistics/overall_count',
    {
      ...config,
      // team_ids is sent as a string array: query params serialize to strings on
      // the wire regardless, Rails coerces the id, and this keeps the type honest
      // (ApiConfig.params only allows string[] for array values — no cast needed).
      params: { team_ids: [String(teamId)] },
      ...(customHeaders && { headers: customHeaders }),
    },
    token,
  );
};

export default getTeamMemberCategoryCountsApi;
